import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

// API Response interfaces
export interface ExportValidation {
  can_export: boolean;
  credits_required: number;
  credits_available: number;
  total_records: number;
  page_size: number;
  message?: string;
}

export interface StartExportResponse {
  export_id: string;
  status: string;
  message: string;
  credits_used?: number;
}

export interface VeridionExport {
  id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  total_records: number;
  total_pages: number;
  pages_completed: number;
  progress_percentage: number;
  credits_used: number;
  is_downloadable: boolean;
  file_name?: string;
  download_url?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface ExportListPagination {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface ExportListResponse {
  data: VeridionExport[];
  pagination: ExportListPagination;
}

export interface ExportListParams {
  status?: string;
  page?: number;
  per_page?: number;
}

@Injectable({
  providedIn: 'root'
})
export class VeridionExportService {
  private apiUrl = environment.api.url;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHttpOptions(): Observable<{ headers: HttpHeaders }> {
    return this.authService.token().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
      }))
    );
  }

  /**
   * Validate export credits availability
   */
  validateExportCredits(totalRecords: number, pageSize?: number): Observable<ExportValidation> {
    const url = `${this.apiUrl}/veridion-export/validate`;
    const payload: any = { total_records: totalRecords };
    if (pageSize) {
      payload.page_size = pageSize;
    }

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, payload, options).pipe(
          map(response => {
            const data = response.data || response;
            return {
              can_export: data.can_export,
              credits_required: data.credits_required,
              credits_available: data.credits_available,
              total_records: data.total_records,
              page_size: data.page_size,
              message: data.message
            };
          }),
          catchError(error => throwError(() => error))
        )
      )
    );
  }

  /**
   * Start a new export job
   */
  startExport(filters: any[], totalRecords: number, pageSize?: number): Observable<StartExportResponse> {
    const url = `${this.apiUrl}/veridion-export/start`;
    const payload: any = {
      filters,
      total_records: totalRecords
    };
    if (pageSize) {
      payload.page_size = pageSize;
    }

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, payload, options).pipe(
          map(response => {
            const data = response.data || response;
            return {
              export_id: data.export_id,
              status: data.status,
              message: data.message,
              credits_used: data.credits_used
            };
          }),
          catchError(error => throwError(() => error))
        )
      )
    );
  }

  /**
   * List all exports with optional filters and pagination
   */
  listExports(params?: ExportListParams): Observable<ExportListResponse> {
    const url = `${this.apiUrl}/veridion-export/list`;

    return this.getHttpOptions().pipe(
      switchMap(options => {
        let httpParams = new HttpParams();
        if (params?.status) {
          httpParams = httpParams.set('status', params.status);
        }
        if (params?.page) {
          httpParams = httpParams.set('page', params.page.toString());
        }
        if (params?.per_page) {
          httpParams = httpParams.set('per_page', params.per_page.toString());
        }

        return this.http.get<any>(url, { ...options, params: httpParams }).pipe(
          map(response => {
            // API returns { data: [...], metadata: {...} }
            const exportsArray = response.data || [];
            const metadata = response.metadata || {};
            return {
              data: exportsArray.map((item: any) => ({
                id: item.id,
                status: item.status,
                total_records: item.total_records,
                total_pages: item.total_pages,
                pages_completed: item.pages_completed,
                progress_percentage: item.progress_percentage,
                credits_used: item.credits_used,
                is_downloadable: item.is_downloadable,
                file_name: item.file_name,
                download_url: item.download_url,
                created_at: item.created_at,
                started_at: item.started_at,
                completed_at: item.completed_at,
                error_message: item.error_message
              })),
              pagination: {
                total: metadata.total || 0,
                per_page: metadata.per_page || 15,
                current_page: metadata.current_page || 1,
                last_page: metadata.last_page || 1
              }
            };
          }),
          catchError(error => throwError(() => error))
        );
      })
    );
  }

  /**
   * Download an export file
   * Returns a Blob that can be saved as a file
   */
  downloadExport(exportId: number): Observable<{ blob: Blob; fileName: string }> {
    const url = `${this.apiUrl}/veridion-export/${exportId}/download`;

    return this.authService.token().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        return this.http.get(url, {
          headers,
          responseType: 'blob',
          observe: 'response'
        }).pipe(
          map(response => {
            const blob = response.body as Blob;
            // Extract filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition');
            let fileName = `veridion_export_${exportId}.zip`;
            if (contentDisposition) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
              if (matches && matches[1]) {
                fileName = matches[1].replace(/['"]/g, '');
              }
            }
            return { blob, fileName };
          }),
          catchError(error => throwError(() => error))
        );
      })
    );
  }
}
